import { RegistrationRecieptCommand } from '../../../registration_receipt/commands/registration_reciept_command';
import { IPreventTransientAccessQueryResult } from '../../../prevent_transient_access/queries/prevent_transient_access_query';
import { IRegistrationWaitlistPusherOutcome } from '../../../interfaces/scheduler_interfaces';
import { RegAttributesForSections } from '../../../interfaces/scheduler_interfaces';
import { IDefineCommand, ICommand, IQuery, IQueryMediator } from 'core-ts-types';
import { ISectionsDatasource } from '../../sections/datasources/sections_datasource';
import { ITermCoursesDatasource } from '../../term_courses/datasources/term_courses_datasource';
import { IRegisterSectionsDatasource } from '../datasources/register_sections_datasource';
import { RegistrationFailureModel } from '../models/registration_failure_model';
import { RegisterSectionsQuery } from '../queries/register_sections_query';
import { SchedulerModel } from '../models/scheduler_model';
import { ScheduledSectionStatus } from '../types/scheduled_section_status_enum';
import { RequisiteCourseServant } from '../servants/requisite_course_servant';
import { RegistrationAlertMessages } from '../types/registration_alert_messages';
import { WaitlistCallbackServant } from '../servants/waitlist_callback_servant';
import { RegistrationCallbackServant } from '../servants/registration_callback_servant';
import { ScheduledSectionServant } from '../servants/scheduled_section_servant';
import { RegisterWaitlistOutcomeFilters } from '../filters/register_waitlist_filters';
import { OUTCOME_STATUS_TYPES } from '../strategies/registration_waitlist_outcome_status_strategy';
import * as _4 from 'lodash4';
import { RegistrationReceiptServant } from '../servants/registration_receipt_servant';
import { UpdateCalendarSectionTimesCommand } from '../../calendar/commands/update_calendar_section_times_command';
import { INotificationDelegate } from '../../../global/helpers/scheduler_notification_delegate';
import { RemoveSectionSelectionCommand } from '../../../components/section_selections/commands/remove_section_selection_command';

export type RegisterScheduledSectionsCommand = (
  termId: number,
  sectionIdsToRegister: number[],
  auto_waitlist_sections: any[],
  section_pins?: Record<string, any>,
  regAttributesForSections?: RegAttributesForSections,
  student_credentials?: IPreventTransientAccessQueryResult['output']
) => ng.IPromise<IQuery[]>;

angular
  .module('capability-ng-scheduler')
  .factory(
    'registerScheduledSectionsCommand',
    (
      $http: ng.IHttpService,
      $q: ng.IQService,
      $timeout: ng.ITimeoutService,
      ngRegistrationAlertsServant: any,
      ngSectionsDatasource: ISectionsDatasource,
      ngTermCoursesDatasource: ITermCoursesDatasource,
      ngRegisterSectionsDatasource: IRegisterSectionsDatasource,
      ngRegisterSectionsQuery: RegisterSectionsQuery,
      ngScheduledSectionServant: ScheduledSectionServant,
      defineCommand: IDefineCommand,
      QueryMediator: IQueryMediator,
      ngSchedulerModel: SchedulerModel,
      ngRegistrationFailureModel: RegistrationFailureModel,
      ngRequisiteCourseServant: RequisiteCourseServant,
      ngWaitlistCallbackServant: WaitlistCallbackServant,
      ngRegistrationCallbackServant: RegistrationCallbackServant,
      ngRegistrationReceiptServant: RegistrationReceiptServant,
      ngRegisterWaitlistOutcomeFilters: RegisterWaitlistOutcomeFilters,
      registrationActivityCommand: ICommand,
      registrationRecieptCommand: RegistrationRecieptCommand,
      updateCalendarSectionTimesCommand: UpdateCalendarSectionTimesCommand,
      appConfigModel: any,
      pusherService: any,
      log: any,
      ngSchedulerNotificationDelegate: INotificationDelegate,
      removeSectionSelectionCommand: RemoveSectionSelectionCommand
    ): ICommand => {
      return defineCommand(
        'registerScheduledSectionsCommand',
        (
          deferred: ng.IDeferred<IQuery[]>,
          termId: number,
          sectionIdsToRegister: number[],
          auto_waitlist_sections: any[],
          section_pins = {},
          regAttributesForSections: RegAttributesForSections = {},
          student_credentials?: IPreventTransientAccessQueryResult['output']
        ) => {
          const SECONDS_60 = 60000;
          const timeout = $q.defer();

          $timeout(() => {
            timeout.resolve();
          }, SECONDS_60);

          if (_4.isEmpty(sectionIdsToRegister)) {
            deferred.reject();
            return;
          }

          const sectionStatusesBeforeRegistration = {};
          _4.each(sectionIdsToRegister, (sectionId: number) => {
            const sctn = ngSectionsDatasource.get(sectionId);
            if (sctn) {
              sectionStatusesBeforeRegistration[sectionId] = sctn.status;
            }
          });

          const showWaitlistMessage = appConfigModel.model.member_features['reg-auto_waitlist_message'];
          const pusher_callback_function = (data: any) => {
            registrationActivityCommand(termId).then(() => {
              const sectionsReceiptData = _4.filter(
                ngRegistrationReceiptServant.getReceiptData(sectionIdsToRegister),
                'isActiveRecord'
              );
              registrationRecieptCommand(sectionsReceiptData, true);
            });
            const registrationOutcomeData = _4.compact(_4.map(sectionIdsToRegister, (id: number) => data[id]));

            const sectionRegistrationOutcome = _4.partition(
              registrationOutcomeData,
              (sectionOutcome: IRegistrationWaitlistPusherOutcome) => sectionOutcome.success
            );
            const selectedSections: any[] = ngRegisterSectionsQuery.result.selectedSections;
            const pusherOutcomes = _4.map(
              registrationOutcomeData,
              (registrationWaitlistOutc: IRegistrationWaitlistPusherOutcome): any => {
                return {
                  id: registrationWaitlistOutc.id,
                  message: registrationWaitlistOutc.message,
                  request_type: 'ADD',
                  success: registrationWaitlistOutc.success,
                  is_registered: registrationWaitlistOutc.is_registered,
                  is_waitlisted: registrationWaitlistOutc.is_waitlisted
                };
              }
            );

            const registerRequestRegistered = ngRegisterWaitlistOutcomeFilters.getFiltered(
              pusherOutcomes,
              OUTCOME_STATUS_TYPES.REGISTRATION_REQUEST_REGISTERED
            );
            const registerRequestWaitlisted = ngRegisterWaitlistOutcomeFilters.getFiltered(
              pusherOutcomes,
              OUTCOME_STATUS_TYPES.REGISTRATION_REQUEST_WAITLISTED
            );
            const registerRequestFailed = ngRegisterWaitlistOutcomeFilters.getFiltered(
              pusherOutcomes,
              OUTCOME_STATUS_TYPES.REGISTRATION_REQUEST_FAILED
            );

            // handle each request group
            _4.each(registerRequestRegistered, (sectionOutcome: IRegistrationWaitlistPusherOutcome) => {
              ngRegistrationCallbackServant.setRegisteredStatus(sectionOutcome);
              ngRegisterSectionsDatasource.del(sectionOutcome.id);
              removeSectionSelectionCommand(sectionOutcome.id);
            });
            _4.each(registerRequestWaitlisted, (sectionOutcome: IRegistrationWaitlistPusherOutcome) => {
              ngWaitlistCallbackServant.waitlistSuccess(sectionOutcome);
              ngRegisterSectionsDatasource.del(sectionOutcome.id);
              removeSectionSelectionCommand(sectionOutcome.id);
            });

            ngRegistrationCallbackServant.showEnforcedCoreqsDropReminderAlerts(registerRequestRegistered);

            ngRegistrationCallbackServant.handlePusherFailedResponse(
              registerRequestFailed,
              sectionStatusesBeforeRegistration
            );

            ngRegistrationAlertsServant.hidePendingAlert();
            updateCalendarSectionTimesCommand();
            // TODO: TECHDEBT
            // this timeout gives calendar times query an additional run after it is delayed by debounce in scheduled sections query,
            // with enough time to allow the calendar to get updated statuses after sections are updated by this callback and calendar model is written
            const RUN_QUERY_DELAY = 350;
            $timeout(() => {
              QueryMediator.runQueries(ngSectionsDatasource);
            }, RUN_QUERY_DELAY);

            deferred.resolve(QueryMediator.runQueries(ngSectionsDatasource, ngTermCoursesDatasource));
            // Show info alerts for non registered sections that are required by these successfully registered sections
            ngRegistrationAlertsServant.showOtherRequiredSectionsAlert(sectionRegistrationOutcome[0]);
          };

          _4.each(sectionIdsToRegister, (id: number) => {
            const section = ngSectionsDatasource.get(id);
            if (section) {
              section.status = ScheduledSectionStatus.PENDING;
              ngSectionsDatasource.put(section);
            }
          });

          $http({
            method: 'POST',
            url: `/reg/register/term/${termId}/`,
            data: {
              method: 'ADD',
              sections: sectionIdsToRegister,
              section_pins,
              ...regAttributesForSections,
              ...student_credentials
            },

            cache: false,
            timeout: timeout.promise
          })
            .then((response: ng.IHttpPromiseCallbackArg<any>) => {
              if (showWaitlistMessage) {
                ngRegistrationAlertsServant.showPendingAlert(
                  RegistrationAlertMessages.PENDING_AUTO_WAITLIST_REGISTERED_TEXT
                );
              } else {
                ngRegistrationAlertsServant.showPendingAlert();
              }
              pusherService.bindToPusherEvent(
                ngSchedulerModel.read().channel_name,
                'registration_outcome_add',
                pusher_callback_function
              );
            })
            .catch(({ data }: ng.IHttpPromiseCallbackArg<any>) => {
              log.issue('failed to register courses, please try again');
              if (_4.has(data, 'detail')) {
                ngSchedulerNotificationDelegate.warn(data.detail);
              }
              _4.each(sectionIdsToRegister, (id: number) => {
                const section = ngSectionsDatasource.get(id);
                if (section) {
                  section.status = ScheduledSectionStatus.SECTION_PICKED;
                  ngSectionsDatasource.put(section);
                }
              });
              QueryMediator.runQueries(ngSectionsDatasource, ngTermCoursesDatasource);
              deferred.reject(data);
            });
        }
      );
    }
  );



// WEBPACK FOOTER //
// ./app/vendor/capability-ng-scheduler/src/views/course_scheduler/commands/register_scheduled_sections_command.ts